import type { ReactNode } from "react";
import { Button } from "../Button";

export interface PageHeaderAction {
  label: string;
  /** Shown beside the label on desktop, and ALONE on mobile. Required. */
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  /** Forwarded to whichever of the two renders is live. */
  testId?: string;
}

export interface PageHeaderProps {
  /** The page's name. Rendered as the `<h1>` unless `headingId` says otherwise. */
  title: ReactNode;
  /** One calm line under the title. Optional — not every page needs one. */
  subtitle?: ReactNode;
  /** A status chip beside the title, for a page whose meaning turns on one state. */
  badge?: ReactNode;
  /**
   * The page's PRIMARY action, declared once and rendered per breakpoint:
   * a full labelled button at `lg`, and an icon-only control in a pill below
   * it. Pass this instead of putting a `<Button>` in `actions` — see the note
   * on the component.
   */
  primaryAction?: PageHeaderAction;
  /**
   * `true` when a SHELL renders the compact copy of `primaryAction` in its own
   * mobile bar. The labelled desktop copy still renders here. Without this the
   * action would appear twice on a phone — once in the bar, once in the page.
   */
  hideActionOnMobile?: boolean;
  /** Secondary page-level actions, placed OUTSIDE the content surface. */
  actions?: ReactNode;
  /** A back affordance. Its presence switches the mobile layout to centred-title. */
  back?: ReactNode;
  /** Id put on the `<h1>`, so a region can be `aria-labelledby` it. */
  headingId?: string;
  /**
   * `true` hides the heading from sight below `lg` — for shells whose mobile top
   * bar already shows the page name. It stays in the accessibility tree.
   */
  hideTitleOnMobile?: boolean;
  className?: string;
}

/**
 * The platform page header: title, optional subtitle, optional status chip, and
 * the page's actions on the inline-end side.
 *
 * ## Why it is here
 *
 * It was written twice. The seller portal had `.page-header` + `PageTitle`
 * (24px/700, actions in a `.page-actions` flex row); the restaurant portal had a
 * private header inside `ManagePage` at 16px/600 with no icon support on its
 * primary action. Every management screen in both apps renders one of these, so
 * the two were the most-repeated visible difference between the products — the
 * page name was a third smaller in one app than the other.
 *
 * ## Actions adapt; they are not just re-styled
 *
 * `primaryAction` is declared ONCE and rendered twice — a labelled button at
 * `lg`, an icon-only control inside a pill below it — with the inactive copy
 * marked `inert` so it leaves the tab order and the accessibility tree rather
 * than becoming a second, invisible "Add" for a screen-reader user.
 *
 * This is the platform's mobile pattern, and it was the thing most obviously
 * missing from the restaurant portal: its page actions kept the full desktop
 * button on a phone, so "إضافة صنف للمنيو" ate a whole row at 390px while the
 * seller collapsed the same action to a `+`. The seller does this on seven
 * screens — and does it by hand on each, with the pill markup copy-pasted
 * seven times. It belongs here, once.
 *
 * The pill geometry is the seller's, to the pixel: `rounded-full` on
 * `--soft2` with 5px padding, holding a 30×33 control.
 *
 * ## Layout
 *
 * Default is a flex row: title block at the start, actions at the end, wrapping
 * on narrow screens rather than crushing the title. With `back`, the mobile
 * layout becomes a three-column grid so the title is TRULY centred between the
 * back affordance and the actions — centring with flex leaves it optically off
 * whenever the two sides differ in width — and reverts to the flex row at `lg`.
 */
export function PageHeader({
  title,
  subtitle,
  badge,
  primaryAction,
  hideActionOnMobile = false,
  actions,
  back,
  headingId,
  hideTitleOnMobile = false,
  className = "",
}: PageHeaderProps) {
  const heading = (
    <h1
      id={headingId}
      data-ui="page-title"
      className={[
        "ui-display text-2xl text-[color:var(--text-primary)]",
        hideTitleOnMobile ? "sr-only lg:not-sr-only" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {title}
    </h1>
  );

  const desktopAction = primaryAction ? (
    <Button
      variant="primary"
      size="lg"
      onClick={primaryAction.onClick}
      disabled={primaryAction.disabled}
      isLoading={primaryAction.isLoading}
      leadingIcon={primaryAction.icon}
      data-testid={primaryAction.testId}
    >
      {primaryAction.label}
    </Button>
  ) : null;

  const compactAction = primaryAction ? (
    <span className="flex items-center gap-1 rounded-full bg-[color:var(--soft2)] p-[5px]">
      <button
        type="button"
        onClick={primaryAction.onClick}
        disabled={primaryAction.disabled}
        aria-label={primaryAction.label}
        data-testid={primaryAction.testId}
        className="flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)] disabled:opacity-50"
      >
        {primaryAction.icon}
      </button>
    </span>
  ) : null;

  /*
   * `data-ui="toolbar"` on the two containers below is a STYLING HOOK for look
   * stylesheets, in the sense `layout-typography-color.md` defines: the ROW is
   * the thing a look needs to address, because what sits in it is a mixture of
   * primitives — a `Button`, a `DateRangePicker` trigger (`data-ui="select"`),
   * sometimes a plain link — and those primitives have different natural
   * heights. A look that draws every control at one height can now say so once
   * instead of every page fixing its own row.
   *
   * It is named for the ROW and not for this component on purpose: a page that
   * builds its own row of filters plus an export button has exactly the same
   * problem and marks it the same way (see `InvoiceHistoryPage`).
   *
   * Not to be confused with `data-ui="page-action"` (singular) on the desktop
   * copy of the PRIMARY action inside this row.
   */
  const actionRow = (
    <>
      {/* Both copies exist so the action is declared ONCE. `hidden` /
          `lg:hidden` is `display: none`, which already removes the inactive
          copy from the tab order AND the accessibility tree — so unlike the
          seller's opacity-based version this needs no `inert` to avoid
          leaving a second, silent "Add" behind for a screen reader. */}
      {compactAction && !hideActionOnMobile ? (
        <span className="lg:hidden">{compactAction}</span>
      ) : null}
      {desktopAction ? (
        <span data-ui="page-action" className="hidden lg:inline-flex">{desktopAction}</span>
      ) : null}
      {actions}
    </>
  );

  const hasActions = Boolean(primaryAction || actions);

  const titleBlock = (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {heading}
        {badge}
      </div>
      {subtitle ? (
        <p data-ui="page-subtitle" className="text-sm text-[color:var(--text-muted)]">{subtitle}</p>
      ) : null}
    </div>
  );

  if (back) {
    return (
      <header
        className={[
          "grid grid-cols-[1fr_auto_1fr] items-center gap-3",
          "lg:flex lg:flex-wrap lg:items-start lg:justify-between lg:gap-4",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="justify-self-start lg:justify-self-auto">{back}</div>
        <div className="min-w-0 text-center lg:text-start">{titleBlock}</div>
        <div
          data-ui="toolbar"
          className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2 justify-self-end lg:ms-auto lg:justify-self-auto"
        >
          {actionRow}
        </div>
      </header>
    );
  }

  return (
    <header
      className={[
        "flex flex-wrap items-start justify-between gap-4",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {titleBlock}
      {hasActions ? (
        <div
          data-ui="toolbar"
          className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2"
        >
          {actionRow}
        </div>
      ) : null}
    </header>
  );
}
